import React, { useRef, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { getLayout, i18nIntl, wrapWithTag } from '../../../lib/ep.js';
import { useCSS } from '../../../lib/hooks.js';
import { validateQuestion } from '../../../lib/qz.js';

import Answer from './Answer';

import VideoEmbed from '../VideoEmbed';

import ActionButton from '../../builder/ActionButton';
import GameWrapper from '../../games/GameWrapper';

const Continue = ({ question, missing, showButton, continueButton, answers, ...props }) => {
	const i18n = (m) => i18nIntl(m, props.intl);

	const is_builder = props?.is_builder ?? false;
	const fake_selected_answer = props?.fake_selected_answer ?? false;

	const is_disabled = (question.canContinue) ? false : true;

	const cButton = () => {
		if(is_disabled) return;
		continueButton();
	};

	let closedAnswers = 0;
	for(var i in answers) {
		if(answers[i].config.type == 'closed_text') closedAnswers++;
	}

	if((question.config.hidecontinue == true && !showButton)) return null;

	let button_classes = ['flex_button', 'gui_flex_button'];
	//if(is_disabled) button_classes.push('is_disabled');

	return (
		<div class="gui_continue_button">
			<div class={button_classes.join(" ")}>
				<ActionButton
					onClick={cButton}
					disabled={is_disabled && (!is_builder || !fake_selected_answer)}
					_fullclass="next_question_btn"
				>Continue</ActionButton>
			</div>

			{(question.type != '1' && closedAnswers > 0 && !props.showFeedback) && (
				<span class="missing_answers">
					{(missing == 0) ? (
						(question.max_answers != null && question.numAnswers >= question.max_answers) && (
							<span class="hb_secondaryFont redColor">{i18n("Maximum number of options selected")}</span>
						)
					) : (
						<span class="hb_secondaryFont hb_mainColorText">{i18n("Options pending to select")}: {missing}</span>
					)}
				</span>
			)}
		</div>
	)
};

const Question = (props) => {
	useCSS('comp/quiz.css');

	const i18n = (m) => i18nIntl(m, props.intl);

	const question = props?.question?.Question ?? {};
	const answers = props?.question?.Answer ?? [];
	const is_vertical = question?.isVertical ?? true;
	const promo = props?.promo ?? {};
	const is_most = props?.isMost ?? false;
	const is_builder = props?.is_builder ?? false;
	const fake_selected_answer = props?.fake_selected_answer ?? false;

	const [ errors, setErrors ] = useState(null);
	const [ answers_row, setAnswersRow ] = useState(question?.answers_row ?? '0');

	const user = useRef(props.user);

	const missing = (question.numAnswers != undefined) ? (question.type != '1' ? Math.max((question.min_answers - question.numAnswers), 0) : 0) : 0;

	const showFeedback = question.show_feedback;
	const is_right = (is_most || (question?.isRight ?? props.isRight)) ? true : false;

	const showRightFeedback = ((question.maxScore > 0 || props.isRight != undefined) && is_most === false) ? true : false;

	const feedback = (is_right) ? question.feedback : (question.config.feedback_wrong ? question.config.feedback_wrong : question.feedback);
	const has_media = (question.image || question.video) ? true : false;

	const points_per_hit = 100;
	const points_per_extra_second = 10;

	const getDefaultAnswersPerRow = () => {
		if(is_vertical) {
			if(answers.length == 1) return 1;
			if([2, 4, 7].includes(answers.length)) return 2;
			if(answers.length / 3 == parseInt(answers.length / 3)) return 3;
			if(answers.length / 4 == parseInt(answers.length / 4)) return 4;
			if(answers.length / 5 == parseInt(answers.length / 5)) return 5;
			return 5;
		}

		if(answers.length <= 3) return 1;
		if(answers.length <= 8) return 2;
		return 3;
	};

	const detectIfDoubleColumnedItems = () => {
		let has_double_columned_item = false;
		for(let i in answers) {
			if(!answers[i].isVertical) {
				has_double_columned_item = true;
				break;
			}
		}

		return has_double_columned_item;
	};

	useEffect(() => {
		if(props?.question?.Question == undefined) return;

		let answers_per_row = props.question.Question.answers_row ?? 3;

		if(answers_per_row == '0') {
			answers_per_row = getDefaultAnswersPerRow();

			if(detectIfDoubleColumnedItems()) {
				answers_per_row = (!is_vertical) ? 1 : Math.max(1, Math.floor(answers_per_row / 2));
			}
		}

		setAnswersRow(answers_per_row);
	}, [ props?.question?.Question.answers_row ]);

	if(Object.keys(question).length == 0) return null;

	/**
	 * Contains all the logic of what happens when you click on an answer
	 * @param  {[type]} e [description]
	 * @param  {[type]} a [description]
	 * @return {[type]}   [description]
	 */
	const answerClick = (e, a) => {
		setErrors(null);

		//Save the clicked answer
		if(props.userAction) {
			user.current = props.userAction(question, a);
		}
	};

	const answerChange = (a, v) => {
		setErrors({});

		if(props.userAction) {
			const res = props.userAction(question, a, v);

			if(res !== false) {
				user.current = res;
			}
		}
	};

	const continueButton = (e, cb) => {
		let _errors = {};

		// ensure default value of the ratings and list fields:
		for(let i in answers) {
			const a = answers[i];

			if(a.config.type == 'rating') {
				if(user.current == undefined || user.current[a.question_id] == undefined || user.current[a.question_id][a.id] == undefined) {
					answerChange(a, a.config.default_rate);
				}
			}

			if(a.config.type == 'list') {
				if(user.current == undefined || user.current[a.question_id] == undefined || user.current[a.question_id][a.id] == undefined) {
					let value = "";

					if(a.config.list_type == 'list') value = a.config.values[0];
					answerChange(a, value);
				}
			}
		}

		// before continuing we must ensure we have filled all answers:
		const res = validateQuestion(props.question, user.current);

		if(!res.status) {
			// set the errors:
			for(let i in res.empty) {
				_errors[res.empty[i]] = "You must fill this field";
			}

			for(let i in res.notvalid) {
				_errors[res.notvalid[i]] = "This is not a valid answer";
			}

			setErrors(_errors);
			return;
		}

		setErrors(_errors);

		if(props.continueAction) {
			props.continueAction(cb);
			return;
		}
	};

	const solveGame = (time, hits, num_attempts, solved, data) => {
		const error_type = data?.error_type ?? false;

		if(props.userAction) {
			const question = props?.question?.Question ?? {};

			if(!solved && (question?.config?.attempts_count_option ?? false) && question.config.attempts <= num_attempts) {
				error_type = 'attempts';
			}

			if(!solved && error_type === false && question.config.time_seconds <= time) {
				error_type = 'time';
			}

			if(!solved && error_type == 'time') {
				// only for time, we set max time defined:
				time = question.config.time_seconds;
			}

			let str = `${time}${data.gp_hash}`;
			let enc = "";
			for(let i in str) {
				if(i == 'contains') continue;

				let c = (str[i]?.charCodeAt(0) << 2) % 123;
				if(c < 32) c += 32;
				enc += String.fromCharCode(c);
			}

			props.userAction(props.question.Question, props.question.Answer[0], {
				time,
				attempts:num_attempts,
				points:pointsGame(question, hits, time, solved, error_type),
				image:data.img,
				gp:data.gp_hash,
				signedRequest:enc,
			});
		}

		return error_type;
	};

	const pointsGame = (question, hits, time, solved, error_type) => {
		const points = hits * ((question.type != 10) ? points_per_hit : 10);

		if((solved === false || solved == 'ko') && ['fail', 'attempts'].includes(error_type)) {
			// no points per time
			return points;
		}

		const time_question = question.config.time_seconds;
		const remaining_seconds = time_question - time;

		return points + remaining_seconds * points_per_extra_second;
	};

	const canvasToBuilder = (options) => {
		if(props.canvasToBuilder == undefined) return;
		props.canvasToBuilder(props.question.Question.id, { ...question.config, ...options });
	};

	if(props.is_game) {
		const is_test = props?.is_test ?? false;
		const has_ranking = (promo.public_entries != undefined && promo.public_entries != '0') ? true : false;
		const layout_props = getLayout(promo);

		return (
			<div id={`q_${question.id}`} style={{ width:'100%', height:'100%' }}>
				<GameWrapper
					options={question}
					stage={props.stage}
					gp_hash={props.gp_hash}
					time_zone={promo.timezone}
					promotion={promo}
					canPlayAgain={props.canPlayAgain}
					main_color={layout_props.color.main_color}
					title={(!question.config.hidequestion) ? question.question : null}
					external_reference={`q_${question.id}`}
					onSolve={solveGame}
					canvasToBuilder={canvasToBuilder}
					has_ranking={has_ranking}
					is_builder={is_builder}
					is_test={is_test}
					forced_screen={props.forced_screen}
					on_continue={continueButton}
					multiparticipation={props.multiparticipation}
					resetQuiz={props.resetQuiz}
					locale={props.locale}
				/>
			</div>
		)
	}

	const question_classes = ['quiz_react'];
	if(is_builder) question_classes.push('layout_conts');
	question_classes.push((question.isVertical || !has_media) ? 'single_columned' : 'double_columned _prop40');

	let answers_container_classes = ['gui_main_text_color_on_bg', 'gui_answers_container', `is_${answers_row}_col`];
	if(question.config.hidecontinue) answers_container_classes.push('is_multianswer');
	if(question.config.force_desktop_layout != undefined && question.config.force_desktop_layout) {
		answers_container_classes.push('force_desktop_layout');
	}

	return (
		<div id={`q_${question.id}`} class={question_classes.join(" ")} style={{width:'100%'}}>
			<div class="flex_cols">
				{(has_media) && (
					<div class="flex_col col_1">
						<div class="mod_align_center main_media_flex">
							{(question.image) && (
								<div class="question_image gui_text_align_center">
									<img src={question.fullimage} alt="" />
								</div>
							)}

							{(question.video) && (
								<VideoEmbed
									video_url={question.video.full_url}
									video_thumb={question.video.image}
								/>
							)}
						</div>
					</div>
				)}

				<div class="flex_col col_2">
					<article class="main_contents_flex">
						<div class="question_cont">
							{(!question.config.hidequestion) && (
								<div
									class="gui_text_block gui_main_text_color_on_bgn question_text"
									dangerouslySetInnerHTML={{ __html:wrapWithTag(question.question, 'h1') }}
								/>
							)}

							<ul class={answers_container_classes.join(" ")}>
								{answers.map((a, i) => {
									const ans = a;
									if(is_builder && fake_selected_answer && i == 0) ans.selected = true;

									return (
										<Answer
											key={i}
											answer={ans}
											errors={errors}
											onClick={answerClick}
											onChange={answerChange}
										/>
									)
								})}
							</ul>
						</div>

						<div class="gui_end_question gui_main_text_color_on_bg">
							{(showFeedback) ? (
								<div class={'gui_feedback ' + ((is_right) ? 'is_ok' : 'is_ko')}>
									{(showRightFeedback) && (
										<h5 class={'result hb_secondaryFont ' + ((is_right) ? 'greenColor' : 'redColor')}>{i18n((is_right) ? "Correct!" : "Incorrect!")}</h5>
									)}

									<div class="content">
										<h4 class="text" dangerouslySetInnerHTML={{ __html:feedback}} />

										<Continue
											question={question}
											answers={answers}
											missing={missing}
											continueButton={continueButton}
											showFeedback={showFeedback}
											showButton={true}
											is_builder={is_builder}
											fake_selected_answer={fake_selected_answer}
											intl={props.intl}
										/>
									</div>
								</div>
							) : (
								<Continue
									question={question}
									answers={answers}
									missing={missing}
									continueButton={continueButton}
									showFeedback={showFeedback}
									is_builder={is_builder}
									fake_selected_answer={fake_selected_answer}
									intl={props.intl}
								/>
							)}
						</div>
					</article>
				</div>
			</div>
		</div>
	)
};

function select(state, props) {
	if(props.id == undefined) return props;

	return {
		question:state.questions.questions.find((q) => q.Question.id == props.id),
	}
};

export default connect(select)(injectIntl(Question));